import { useState } from "react";
import axios from "axios";
import { Copy, ExternalLink, Loader2, Pause, Play, QrCode, Smartphone } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CT } from "@/lib/connectionsTheme";

const FORMATS = ["png", "jpg", "svg"];
const STYLE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "standard", label: "Standard" },
  { value: "branded", label: "Branded" },
];

export const QRCodePreviewModal = ({ qr, onClose, onChanged }) => {
  const { API } = useAuth();
  const [view, setView] = useState("code"); // code | mobile
  const [format, setFormat] = useState("png");
  const [name, setName] = useState(qr.name);
  const [style, setStyle] = useState(qr.style);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const imageUrl = `${API}/org/intake/qr-codes/${qr.qr_id}/image?fmt=${format}&v=${qr.updated_at}`;

  const saveChanges = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/org/intake/qr-codes/${qr.qr_id}`, { name: name.trim(), style }, { withCredentials: true });
      onChanged(data.qr);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save changes.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    setError("");
    try {
      const action = qr.active ? "deactivate" : "reactivate";
      const { data } = await axios.post(`${API}/org/intake/qr-codes/${qr.qr_id}/${action}`, {}, { withCredentials: true });
      onChanged(data.qr);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(qr.public_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const link = document.createElement("a");
    link.href = `${imageUrl}&download=true`;
    link.download = `${qr.name}-qr.${format}`;
    link.click();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border border-slate-200 bg-white text-slate-900 dark:border-blue-500/20 dark:bg-[#0F172A] dark:text-white sm:max-w-lg" data-testid="qr-preview-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-white">{qr.name}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1.5 rounded-lg border border-slate-200 p-1 dark:border-white/10" data-testid="qr-preview-view-tabs">
          <button
            type="button"
            onClick={() => setView("code")}
            data-testid="qr-preview-view-code-tab"
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold transition-colors duration-150 ${view === "code" ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}
          >
            <QrCode size={13} /> QR Code
          </button>
          <button
            type="button"
            onClick={() => setView("mobile")}
            data-testid="qr-preview-view-mobile-tab"
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold transition-colors duration-150 ${view === "mobile" ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}
          >
            <Smartphone size={13} /> Mobile Preview
          </button>
        </div>

        {view === "mobile" ? (
          <div className="flex flex-col items-center gap-3" data-testid="qr-preview-mobile-panel">
            <p className="text-center text-xs text-slate-500 dark:text-slate-400">This is exactly what a customer sees after scanning. Try it out — it's fully interactive.</p>
            <div className="rounded-[2rem] border-[6px] border-slate-900 bg-slate-900 p-1.5 shadow-xl dark:border-slate-700">
              <div className="mx-auto mb-1 h-1.5 w-16 rounded-full bg-slate-700" />
              <div className="h-[560px] w-[290px] overflow-hidden rounded-[1.4rem] bg-white">
                <iframe src={qr.public_url} title="Mobile intake preview" className="h-full w-full border-0" data-testid="qr-preview-mobile-iframe" />
              </div>
            </div>
            <a href={qr.public_url} target="_blank" rel="noreferrer" data-testid="qr-preview-open-new-tab-link" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:underline dark:text-blue-400">
              <ExternalLink size={12} /> Open in a new tab
            </a>
          </div>
        ) : (
        <>
        <div className="flex flex-col items-center gap-3">
          <div className="flex gap-1.5 rounded-lg border border-slate-200 p-1 dark:border-white/10">
            {FORMATS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                data-testid={`qr-preview-format-${f}`}
                className={`rounded-md px-3 py-1 text-xs font-bold uppercase transition-colors duration-150 ${format === f ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex h-48 w-48 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white p-3 dark:border-white/15">
            <img src={imageUrl} alt={`QR code for ${qr.name}`} className="h-full w-full object-contain" data-testid="qr-preview-image" />
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={copyLink} data-testid="qr-preview-copy-btn" className={CT.btnSecondary}>
              <Copy size={12} /> {copied ? "Copied!" : "Copy Link"}
            </button>
            <button type="button" onClick={download} data-testid="qr-preview-download-btn" className={CT.btnPrimary}>
              Download .{format}
            </button>
          </div>
          <p className="break-all text-center text-xs text-slate-500 dark:text-slate-500" data-testid="qr-preview-url">{qr.public_url}</p>
        </div>

        <div className="space-y-3 border-t border-slate-200 pt-4 dark:border-white/10">
          <div>
            <label className={CT.label}>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="qr-preview-name-input" className={`${CT.input} mt-1`} />
          </div>
          <div>
            <label className={CT.label}>Style</label>
            <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="qr-preview-style-select" className={`${CT.input} mt-1`}>
              {STYLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className="text-xs font-semibold text-red-600 dark:text-red-400" data-testid="qr-preview-error">{error}</p>}

        {style === "branded" && !qr.logo_available && (
          <p className="text-xs font-semibold text-amber-500" data-testid="qr-preview-no-logo-notice">
            No organization logo set — this QR will render as Standard until one is added.
          </p>
        )}

        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={toggleActive} disabled={busy} data-testid="qr-preview-toggle-active-btn" className={CT.btnSecondary}>
            {qr.active ? <><Pause size={12} /> Deactivate</> : <><Play size={12} /> Reactivate</>}
          </button>
          <button type="button" onClick={saveChanges} disabled={busy} data-testid="qr-preview-save-btn" className={CT.btnPrimary}>
            {busy && <Loader2 size={14} className="animate-spin" />} Save Changes
          </button>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
